import { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Page,
  Header,
  Content,
  Table,
  TableColumn,
  Link,
  HeaderLabel,
} from '@backstage/core-components';
import {
  useApi,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/core-plugin-api';
import {
  Chip,
  makeStyles,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
} from '@material-ui/core';

type SourceType = 'Service' | 'ArgoCD Application';

interface ServiceListItem {
  name: string;
  team?: string;
  environments: string[];
  hasVaultPolicy: boolean;
  hasTerraform: boolean;
  sourceType: SourceType;
}

const useStyles = makeStyles(theme => ({
  envChip: {
    margin: 2,
    fontWeight: 500,
    fontSize: '0.75rem',
  },
  badge: {
    margin: 2,
    color: '#fff',
    fontWeight: 500,
    fontSize: '0.7rem',
  },
  filterBar: {
    marginBottom: theme.spacing(2),
  },
  teamSelect: {
    minWidth: 220,
  },
}));

const ENV_COLORS: Record<string, string> = {
  prod: '#d32f2f',
  production: '#d32f2f',
  stg: '#f57c00',
  staging: '#f57c00',
  dev: '#388e3c',
  ci: '#1976d2',
  uat: '#7b1fa2',
};

function envColor(envName: string): string {
  const lower = envName.toLowerCase();
  for (const [key, color] of Object.entries(ENV_COLORS)) {
    if (lower.startsWith(key)) return color;
  }
  return '#607d8b';
}

export const ServicesListPage = () => {
  const classes = useStyles();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [services, setServices] = useState<ServiceListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [selectedTeam, setSelectedTeam] = useState<string>('');
  const [selectedType, setSelectedType] = useState<string>('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const baseUrl = await discoveryApi.getBaseUrl('gdp-services');
      const response = await fetchApi.fetch(`${baseUrl}/services`);
      if (!response.ok) throw new Error(`${response.statusText}`);
      setServices(await response.json());
      setError(undefined);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [discoveryApi, fetchApi]);

  useEffect(() => {
    load();
  }, [load]);

  const teams = useMemo(
    () =>
      Array.from(new Set(services.map(s => s.team).filter(Boolean) as string[])).sort(),
    [services],
  );

  const filteredServices = useMemo(
    () =>
      services.filter(s => {
        if (selectedTeam && s.team !== selectedTeam) return false;
        if (selectedType && s.sourceType !== selectedType) return false;
        return true;
      }),
    [services, selectedTeam, selectedType],
  );

  const SOURCE_TYPE_COLORS: Record<string, string> = {
    Service: '#1976d2',
    'ArgoCD Application': '#ef7b4d',
  };

  const columns: TableColumn<ServiceListItem>[] = [
    {
      title: 'Name',
      field: 'name',
      highlight: true,
      render: row => (
        <Link to={`/gdp-services/${row.name}?sourceType=${encodeURIComponent(row.sourceType)}`}>
          {row.name}
        </Link>
      ),
    },
    {
      title: 'Type',
      field: 'sourceType',
      render: row => (
        <Chip
          label={row.sourceType}
          size="small"
          className={classes.badge}
          style={{ backgroundColor: SOURCE_TYPE_COLORS[row.sourceType] ?? '#607d8b' }}
        />
      ),
    },
    {
      title: 'Team',
      field: 'team',
      render: row => row.team ?? '—',
    },
    {
      title: 'Environments',
      field: 'environments',
      sorting: false,
      render: row => (
        <>
          {row.environments.map(env => (
            <Chip
              key={env}
              label={env}
              size="small"
              className={classes.envChip}
              style={{ backgroundColor: envColor(env), color: '#fff' }}
            />
          ))}
        </>
      ),
    },
    {
      title: 'Infra',
      sorting: false,
      render: row => (
        <>
          {row.hasVaultPolicy && (
            <Chip
              label="Vault"
              size="small"
              className={classes.badge}
              style={{ backgroundColor: '#000' }}
            />
          )}
          {row.hasTerraform && (
            <Chip
              label="Terraform"
              size="small"
              className={classes.badge}
              style={{ backgroundColor: '#5c4ee5' }}
            />
          )}
        </>
      ),
    },
  ];

  if (error) {
    return (
      <Page themeId="tool">
        <Header title="GDP Services" />
        <Content>
          <p>Error: {error}</p>
        </Content>
      </Page>
    );
  }

  return (
    <Page themeId="tool">
      <Header title="GDP Services" subtitle="Services and ArgoCD ApplicationSets">
        <HeaderLabel label="Total" value={String(services.length)} />
      </Header>
      <Content>
        <Box className={classes.filterBar} display="flex" style={{ gap: 16 }}>
          <FormControl variant="outlined" size="small" className={classes.teamSelect}>
            <InputLabel>Type</InputLabel>
            <Select
              value={selectedType}
              onChange={e => setSelectedType(e.target.value as string)}
              label="Type"
            >
              <MenuItem value="">All Types</MenuItem>
              <MenuItem value="Service">Service</MenuItem>
              <MenuItem value="ArgoCD Application">ArgoCD Application</MenuItem>
            </Select>
          </FormControl>
          <FormControl variant="outlined" size="small" className={classes.teamSelect}>
            <InputLabel>Team</InputLabel>
            <Select
              value={selectedTeam}
              onChange={e => setSelectedTeam(e.target.value as string)}
              label="Team"
            >
              <MenuItem value="">All Teams</MenuItem>
              {teams.map(t => (
                <MenuItem key={t} value={t}>
                  {t}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Table
          title={
            selectedTeam || selectedType
              ? `Filtered (${filteredServices.length})`
              : `All (${filteredServices.length})`
          }
          columns={columns}
          data={filteredServices}
          isLoading={loading}
          options={{
            search: true,
            paging: true,
            pageSize: 25,
            pageSizeOptions: [25, 50, 100],
            padding: 'dense',
          }}
        />
      </Content>
    </Page>
  );
};
